// component/QuotationTable.js
import React from "react";

const QuotationTable = ({
  quotations,
  sortConfig,
  handleSort,
  getSortIndicator,
}) => {
  const headers = [
    "runNumber",
    "type",
    "period",
    "title",
    "client",
    "documentDate",
    "approvalStatus",
    "createdByUser",
    "total", // ⭐ NEW — Total Before VAT
    "netAmount",
  ];

  const getHeaderLabel = (key) => {
    switch (key) {
      case "runNumber":
        return "Run Number";
      case "documentDate":
        return "Document Date";
      case "approvalStatus":
        return "Approval Status";
      case "createdByUser":
        return "Created By";
      case "netAmount":
        return "Net Amount";
      case "total":
        return "Total Before VAT"; // ⭐ Label ใหม่
      case "period":
        return "Project Run";
      default:
        return key.charAt(0).toUpperCase() + key.slice(1);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-200 text-green-900 dark:bg-green-900/40 dark:text-green-300";
      case "Pending":
        return "bg-yellow-200 text-yellow-900 dark:bg-yellow-900/40 dark:text-yellow-300";
      case "Rejected":
        return "bg-red-200 text-red-900 dark:bg-red-900/40 dark:text-red-300";
      case "Canceled":
        return "bg-gray-300 text-gray-900 dark:bg-gray-700 dark:text-gray-300";
      default:
        return "bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide">
            <tr>
              {headers.map((key) => (
                <th
                  key={key}
                  className="px-3 py-3 font-semibold cursor-pointer select-none hover:text-indigo-500 dark:hover:text-indigo-400"
                  onClick={() => handleSort(key)}
                >
                  <div className="flex items-center gap-1">
                    {getHeaderLabel(key)}
                    {getSortIndicator(key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {quotations.map((q, idx) => (
              <tr
                key={q._id}
                className={`transition-all ${
                  idx % 2 === 0
                    ? "bg-white dark:bg-gray-900"
                    : "bg-gray-50 dark:bg-gray-800"
                } hover:bg-gray-100 dark:hover:bg-indigo-900/20`}
              >
                <td className="px-3 py-2">{q.runNumber}</td>
                <td className="px-3 py-2">{q.type}</td>
                <td className="px-3 py-2">{q.period || "-"}</td>

                {/* Title */}
                <td className="px-3 py-2">
                  <div className="relative inline-block group">
                    <span className="block max-w-[240px] truncate">
                      {q.title?.length > 30
                        ? q.title.slice(0, 30) + "..."
                        : q.title || "-"}
                    </span>
                    {q.title?.length > 30 && (
                      <div className="absolute left-0 top-[calc(100%+6px)] z-50 hidden group-hover:block">
                        <div className="max-w-[520px] whitespace-normal rounded-md bg-gray-900 text-white text-xs px-3 py-2 shadow-lg">
                          {q.title}
                        </div>
                      </div>
                    )}
                  </div>
                </td>

                {/* Client */}
                <td className="px-3 py-2">
                  <div className="relative inline-block group">
                    <span className="block max-w-[240px] truncate">
                      {q.client?.length > 30
                        ? q.client.slice(0, 30) + "..."
                        : q.client || "N/A"}
                    </span>
                    {q.client?.length > 30 && (
                      <div className="absolute left-0 top-[calc(100%+6px)] z-50 hidden group-hover:block">
                        <div className="max-w-[520px] whitespace-normal rounded-md bg-gray-900 text-white text-xs px-3 py-2 shadow-lg">
                          {q.client}
                        </div>
                      </div>
                    )}
                  </div>
                </td>

                <td className="px-3 py-2">
                  {q.documentDate
                    ? new Date(q.documentDate).toLocaleDateString("en-GB")
                    : "-"}
                </td>

                <td className="px-3 py-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(
                      q.approvalStatus
                    )}`}
                  >
                    {q.approvalStatus}
                  </span>
                </td>

                <td className="px-3 py-2">
                  {q.createdByUser ? q.createdByUser.split("@")[0] : "-"}
                </td>

                {/* ⭐ New Total Before VAT */}
                <td className="px-3 py-2 text-right font-medium">
                  {q.total
                    ? q.total.toLocaleString("th-TH", {
                        style: "currency",
                        currency: "THB",
                      })
                    : "-"}
                </td>

                {/* Net Amount */}
                <td className="px-3 py-2 text-right font-medium">
                  {q.netAmount
                    ? q.netAmount.toLocaleString("th-TH", {
                        style: "currency",
                        currency: "THB",
                      })
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE */}
      <div className="md:hidden space-y-4">
        {quotations.map((q) => (
          <div
            key={q._id}
            className="border rounded-lg shadow-sm p-4 bg-white dark:bg-gray-900 text-sm space-y-1"
          >
            <div>
              <span className="font-semibold">Run Number:</span> {q.runNumber}
            </div>

            <div>
              <span className="font-semibold">Type:</span> {q.type}
            </div>

            <div>
              <span className="font-semibold">Project Run:</span>{" "}
              {q.period || "-"}
            </div>

            <div>
              <span className="font-semibold">Title:</span> {q.title || "-"}
            </div>

            <div>
              <span className="font-semibold">Client:</span> {q.client || "N/A"}
            </div>

            <div>
              <span className="font-semibold">Date:</span>{" "}
              {q.documentDate
                ? new Date(q.documentDate).toLocaleDateString("th-TH")
                : "-"}
            </div>

            <div>
              <span className="font-semibold">Created By:</span>{" "}
              {q.createdByUser ? q.createdByUser.split("@")[0] : "-"}
            </div>

            {/* ⭐ Total Before VAT */}
            <div className="mt-2">
              <span className="font-semibold">Total Before VAT:</span>{" "}
              {q.total
                ? q.total.toLocaleString("th-TH", {
                    style: "currency",
                    currency: "THB",
                  })
                : "-"}
            </div>

            {/* ⭐ Net Amount */}
            <div>
              <span className="font-semibold">Net Amount:</span>{" "}
              {q.netAmount
                ? q.netAmount.toLocaleString("th-TH", {
                    style: "currency",
                    currency: "THB",
                  })
                : "-"}
            </div>

            {/* STATUS */}
            <div className="mt-2">
              <span className="font-semibold">Status:</span>{" "}
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(
                  q.approvalStatus
                )}`}
              >
                {q.approvalStatus}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default QuotationTable;
